import _ from 'lodash'
import io from 'socket.io-client'

import AuthService from '@/services/authService'
import SystemLogger from '@/utils/systemLogger'


let SocketMgrMessages = {
  subscribeEndpoint: 'subscribeEndpoint',
  unsubscribeEndpoint: 'unsubscribeEndpoint',
  refreshEndpoint: 'refreshEndpoint',
  subscribeRoom: 'subscribeRoom',
  unsubscribeRoom: 'unsubscribeRoom',
  locationUpdated: 'locationUpdated',
  webNotification: 'webNotification'
}
let SocketMgrRooms = {
  locationLiveUpdates: 'locationLiveUpdates'
}

/**
 * Socket Service
 * Service around the websocket communications (based on socket.io) between client and server
 * Used for subscribing to endpoint updates and the chat messaging system in future
 */
export default class SocketService {
  constructor(url) {
    this.socketUrl = url
    this.socket = null
    this.subscriptions = []
    this.roomSubscriptions = []
    this.eventListeners = []

    this.SocketMgrMessages = SocketMgrMessages
    this.SocketMgrRooms = SocketMgrRooms
  }

  connect() {
    let authService = new AuthService()

    if(!authService.hasAuthSession()) {
      // Login process will handle calling connect()
      return
    }

    if(this.socket != null)
      return

    var socketHost = this.socketUrl

    this.socket = io(socketHost, {
      transports: [ 'websocket' ],      // Only websockets to avoid issues with load balancers
      query: 'auth=' + authService.getAuthToken(),
      upgrade: false,
      closeOnBeforeunload: false        // Fixes issue with save dialog disconnecting socket
    })

    let socketService = this

    this.socket.on('connect', function(s) {
      SystemLogger.logDebugMessage(`[SOCKET] Connected`)

      // Tell server about all our subscriptions
      socketService.subscriptions.forEach(sub => {
        socketService.socket.emit(SocketMgrMessages.subscribeEndpoint, sub.endpoint)
      })
      socketService.roomSubscriptions.forEach(sub => {
        socketService.socket.emit(SocketMgrMessages.subscribeRoom, sub.room)
      })

      // Note: Don't need to re-add event listeners, only subscriptions. Event listeners get handled automatically via socket.io
    })
    this.socket.on('disconnect', function(s) {
      SystemLogger.logDebugMessage(`[SOCKET] Disconnect`)
    })

    this.socket.on('connect_error', (info) => {
      console.error(`[SOCKET] Failed to connect: ${info}`)
    })
    this.socket.on('connect_timeout', (info) => {
      console.error(`[SOCKET] Connection timeout: ${info}`)
    })
    
    this.socket.on(SocketMgrMessages.refreshEndpoint, function(endpoint, options) {
      SystemLogger.logDebugMessage(`[SOCKET] RefreshEndpoint('${endpoint}', ${JSON.stringify(options)})`)

      if(options.delay) {
        // Delay if requested
        // Bit hacky, but fixes delays in the server updates / database replications finishing after the endpoint refresh sent
        setTimeout(() => {
          socketService.subscriptions.forEach(sub => {
            if(sub.endpoint == endpoint) {
              sub.callback(options.data || {})
            }
          })
        }, options.delay)
      } else {
        socketService.subscriptions.forEach(sub => {
          if(sub.endpoint == endpoint) {
            sub.callback(options.data || {})
          }
        })
      }
    })

    this.socket.on(SocketMgrMessages.webNotification, function(message) {
      socketService.handleWebNotificationReceived(message)
    })
  }


  /**
   * Disconnect the socket service
   * 
   */
  disconnect() {
    if(this.socket != null) {
      this.socket.disconnect()
      this.socket = null
    }

    // Clear everything
    this.subscriptions = []
    this.roomSubscriptions = []
    this.eventListeners = []
  }



  /**
   * Subscribe to an endpoint and receive a notification to refresh it when it's updated
   * 
   * @param {VueComponent} component - Vue component subscribing to the notification
   * @param {String} endpoint - endpoint (with leading slash) that is used in the REST API to listen to updates for
   * @param {Function} callback - callback to call when the endpoint has been updated
   */
  subscribeEndpoint(component, endpoint, callback) {
    // Notify server
    if(this.socket && this.socket.connected) {
      this.socket.emit(SocketMgrMessages.subscribeEndpoint, endpoint)
    }

    this.subscriptions.push({
      component: component,
      endpoint: endpoint,
      callback: callback
    })
  }


  /**
   * Unsubscribe to an endpoint that was previously subscribed to
   * 
   * @param {VueComponent} component - Vue component that was subscribed to the notification
   * @param {String} endpoint - endpoint (with leading slash) that is used in the REST API to listen to updates for
   */
  unsubscribeEndpoint(component, endpoint) {

    // Notify server
    if(this.socket && this.socket.connected) {
      this.socket.emit(SocketMgrMessages.unsubscribeEndpoint, endpoint)
    }

    this.subscriptions = this.subscriptions.filter(sub => {
      return !(sub.component == component && sub.endpoint == endpoint)
    })
  }


  /**
   * Subscribe to a room to listen to all events posted to the room
   * Note: Events must be explicitly listened to via addSocketEventListener() to listen to events posted to rooms
   * 
   * @param {VueComponent} component - Vue component subscribing to the room
   * @param {String} room - Name of the room to subscribe/join
   */
  subscribeRoom(component, room) {
    // Notify server
    if(this.socket && this.socket.connected) {
      this.socket.emit(SocketMgrMessages.subscribeRoom, room)
    }

    // Only 1 room sub per component/room combo
    if(this.roomSubscriptions.filter(sub => sub.component == component && sub.room == room).length == 0) {
      this.roomSubscriptions.push({
        component: component,
        room: room
      })
    }
  }


  /**
   * Unsubscribe to a room that was previously subscribed to
   * 
   * @param {VueComponent} component - Vue component that was subscribed to the room
   * @param {String} room - Name of the room to leave/unsubscribe
   */
  unsubscribeRoom(component, room) {
    this.roomSubscriptions = this.roomSubscriptions.filter(sub => {
      return !(sub.component == component && sub.room == room)
    })

    // If no more subscriptions to this room, then notify server to leave the room
    if(this.roomSubscriptions.filter(sub => sub.component == component && sub.room == room).length == 0) {

      // Notify server
      if(this.socket && this.socket.connected) {
        this.socket.emit(SocketMgrMessages.unsubscribeRoom, room)
      }
    }
  }


  /**
   * Add a listener for a particular event
   * When the event has been posted to this socket (or broadcast globally / in a room) the callback will be called with the event args
   * Note: To listen to events posted in rooms, the room must ALSO be joined via subscribeRoom()
   * 
   * @param {VueComponent} component - Vue component that is adding a listener to an event
   * @param {String} event - Name of the event to listen to
   * @param {Function} callback - callback to call when the event has been received
   */
  addSocketEventListener(component, event, callback) {
    // Add listener
    if(this.socket) {
      this.socket.on(event, callback)
    }
    this.eventListeners.push({
      component: component,
      event: event,
      callback: callback
    })
  }


  /**
   * Remove a listener on the socket for a particular event
   * Assumes the event had an event listener added already via addSocketEventListener()
   * 
   * @param {VueComponent} component - Vue component that was listening to the event
   * @param {String} event - Name of the event to remove the listener for
   */
  removeSocketEventListener(component, event) {
    if(this.socket) {
      this.eventListeners
      .filter(listener => listener.component == component && listener.event == event)
      .forEach(listener => {
        this.socket.off(listener.event, listener.callback)
      })
    }

    this.eventListeners = this.eventListeners.filter(listener => {
      return !(listener.component == component && listener.event == event)
    })
  }


  /**
   * Handle receiving a web notification from the socket service system
   * Note: This is for simple web notifications done directly. We don't support the full WebPush system
   * 
   * @param {Objects} messageData - Message contents from the server
   */
  handleWebNotificationReceived(messageData) {
    try {
      if(!messageData)
        return

      // Ignore if notifications not supported on the browser
      if(!('Notification' in window))
        return

      if (Notification.permission === 'granted') {
        this._createWebNotification(messageData)

      } else if(Notification.permission !== 'denied') {
        // Request permission
        Notification.requestPermission().then(function (permission) {
          if (permission === "granted") {
            this._createWebNotification(messageData)
          }
        })
      }

    } catch(err) {
    }
  }


  /**
   * Setup & Create the notification object. Called once permissions have been granted
   * Called internally from handleWebNotificationReceived()
   * 
   * @param {Object} messageData - Message data from the server
   */
  _createWebNotification(messageData) {
    let notificationOptions = {
      body: messageData.body,
    }

    let notificationObj = new Notification(messageData.title || '', notificationOptions)

    if(messageData.link) {
      notificationObj.onclick = function(event) {
        event.preventDefault(); // prevent the browser from focusing the Notification's tab
        window.open(messageData.link, '_blank');
      }
    }
  
    setTimeout(notificationObj.close.bind(notificationObj), 7000)
  }
}